import React, { useState, useEffect } from 'react';
import StandardPremiumModal from './StandardPremiumModal';
import { useBusiness } from '../../contexts/BusinessContext';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const AddEditSalesReturnModal = ({ isOpen, onClose, salesReturn, onSuccess }) => {
  const { activeBusiness } = useBusiness();
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    return_date: new Date().toISOString().split('T')[0],
    items: [],
    return_reason: '',
    refund_mode: 'CASH',
    notes: ''
  });

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchCustomers();
      fetchProducts();
    }
  }, [activeBusiness]);

  useEffect(() => {
    if (salesReturn) {
      setFormData({
        customer_id: salesReturn.customer_id || '',
        return_date: salesReturn.return_date || new Date().toISOString().split('T')[0],
        items: salesReturn.items || [],
        return_reason: salesReturn.return_reason || '',
        refund_mode: salesReturn.refund_mode || 'CASH',
        notes: salesReturn.notes || ''
      });
    } else {
      setFormData({
        customer_id: '',
        return_date: new Date().toISOString().split('T')[0],
        items: [],
        return_reason: '',
        refund_mode: 'CASH',
        notes: ''
      });
    }
  }, [salesReturn, isOpen]);

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'customer' } });
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await api.get('/items');
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item_id: '',
          item_name: '',
          quantity: 1,
          unit: 'Nos',
          rate: 0,
          amount: 0,
          reason: ''
        }
      ]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index][field] = value;

      // If item_id changed, update item details
      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].rate = product.sale_price || 0;
          newItems[index].unit = product.unit || 'Nos';
        }
      }

      // Calculate amount
      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = qty * rate;

      return { ...prev, items: newItems };
    });
  };

  const handleSave = async () => {
    // Validate
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (!formData.return_reason) {
      toast.error('Please provide a return reason');
      return;
    }

    setSaving(true);
    try {
      if (salesReturn) {
        await api.put(`/sales-returns/${salesReturn.id}`, formData);
      } else {
        await api.post('/sales-returns', formData);
      }
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Error saving sales return');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        disabled={saving}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
      >
        {saving ? 'Saving...' : salesReturn ? 'Update Return' : 'Create Return'}
      </button>
    </>
  );

  const grandTotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <StandardPremiumModal
      isOpen={isOpen}
      onClose={onClose}
      title={salesReturn ? 'Edit Sales Return' : 'Add Sales Return'}
      footer={footer}
      size="large"
    >
      <div className="space-y-6">
        {/* Basic Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.customer_id}
              onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.return_date}
              onChange={(e) => setFormData(prev => ({ ...prev, return_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refund Mode <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.refund_mode}
              onChange={(e) => setFormData(prev => ({ ...prev, refund_mode: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="CASH">Cash</option>
              <option value="BANK">Bank Transfer</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </select>
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Items <span className="text-red-500">*</span>
            </label>
            <button
              onClick={addLineItem}
              className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-xs"
            >
              <Plus className="w-3 h-3" />
              Add Item
            </button>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {formData.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="grid grid-cols-12 gap-2 mb-2">
                  <div className="col-span-4">
                    <select
                      value={item.item_id}
                      onChange={(e) => updateLineItem(index, 'item_id', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                      placeholder="Qty"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.rate}
                      onChange={(e) => updateLineItem(index, 'rate', e.target.value)}
                      placeholder="Rate"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm font-medium">₹{item.amount.toFixed(2)}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end">
                    <button
                      onClick={() => removeLineItem(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="col-span-12">
                  <input
                    type="text"
                    value={item.reason || ''}
                    onChange={(e) => updateLineItem(index, 'reason', e.target.value)}
                    placeholder="Reason for returning this item..."
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            ))}
            {formData.items.length === 0 && (
              <div className="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg">
                No items added. Click "Add Item" to add products.
              </div>
            )}
          </div>
        </div>

        {/* Total */}
        <div className="flex justify-end">
          <div className="text-right">
            <div className="text-sm text-gray-600">Total Return Amount:</div>
            <div className="text-2xl font-bold text-red-600">₹{grandTotal.toFixed(2)}</div>
          </div>
        </div>

        {/* Return Reason & Notes */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.return_reason}
              onChange={(e) => setFormData(prev => ({ ...prev, return_reason: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Reason for this return..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Additional notes..."
            />
          </div>
        </div>
      </div>
    </StandardPremiumModal>
  );
};

export default AddEditSalesReturnModal;
